import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip, Spinner } from "flowbite-react";
import {
  HiOutlineInformationCircle,
  HiChevronDown,
  HiChevronUp,
} from "react-icons/hi";
import { useAccountStates } from "../../hooks";
import { TooltipContent } from "../TooltipContent/TooltipContent";
import { SignalCard } from "./SignalCard";
import { LifecycleCard } from "./LifecycleCard";
import { PiPulse } from "react-icons/pi";
import { VscGraph } from "react-icons/vsc";
import { LuClock } from "react-icons/lu";
import { FaArrowTrendDown } from "react-icons/fa6";
import { CiChat1 } from "react-icons/ci";

// Signal categories in display order
const SIGNAL_CATEGORIES = [
  "usage_pattern",
  "feature_usage",
  "support_activity",
  "support_response",
  "support_status",
  "communication",
];

const SIGNAL_ICONS = {
  usage_pattern: PiPulse,
  feature_usage: VscGraph,
  support_activity: LuClock,
  support_response: HiOutlineInformationCircle,
  support_status: FaArrowTrendDown,
  communication: CiChat1,
};

export const AccountStates = ({ accountId }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const { data, loading, error } = useAccountStates(accountId);

  // Filter to only categories that have data
  const categoriesWithData = SIGNAL_CATEGORIES.filter(
    (category) => data?.[category] && Object.keys(data[category]).length > 0,
  );

  const lifecycleCards = data?.account_lifecycle || [];

  // Hide entire section if no data available
  if (
    !loading &&
    !error &&
    categoriesWithData.length === 0 &&
    lifecycleCards.length === 0
  ) {
    return null;
  }

  return (
    <div>
      {/* Header outside card */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PiPulse className="size-5 text-green-500" />
          <h2 className="text-base font-semibold text-gray-900">
            {t("pages.customer.accountStates.title", "Account states")}
          </h2>
          <Tooltip
            content={
              <TooltipContent
                title={t(
                  "pages.customer.accountStates.tooltipTitle",
                  "About Account States",
                )}
                text={t(
                  "pages.customer.accountStates.tooltipText",
                  "Signal metrics showing key account health indicators across usage, support, and communication.",
                )}
              />
            }
            // eslint-disable-next-line react/style-prop-object
            style="light"
          >
            <HiOutlineInformationCircle className="h-3.5 w-3.5 text-gray-400" />
          </Tooltip>
        </div>

        {categoriesWithData.length > 3 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            {isExpanded
              ? t("pages.customer.accountStates.showLess", "Show less")
              : t("pages.customer.accountStates.showMore", "Show more")}
            {isExpanded ? (
              <HiChevronUp className="h-3.5 w-3.5" />
            ) : (
              <HiChevronDown className="h-3.5 w-3.5" />
            )}
          </button>
        )}
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-red-500">
            {t(
              "pages.customer.accountStates.error",
              "Failed to load account states",
            )}
          </p>
        </div>
      )}

      {/* Lifecycle state cards (communicated churn, etc.) — shown above signal cards */}
      {!loading && !error && lifecycleCards.length > 0 && (
        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {lifecycleCards.map((card) => (
            <LifecycleCard key={card.type} card={card} />
          ))}
        </div>
      )}

      {/* Grid of signal cards - only show categories with data */}
      {!loading && !error && categoriesWithData.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {(isExpanded
            ? categoriesWithData
            : categoriesWithData.slice(0, 3)
          ).map((category) => (
            <SignalCard
              key={category}
              category={category}
              data={data[category]}
              icon={SIGNAL_ICONS[category]}
            />
          ))}
        </div>
      )}
    </div>
  );
};
